import { createContext, useCallback, useContext, useEffect } from "react";
import type {
	AudioPlaybackService,
	BlendedAudioPlaybackService,
	PlaybackStatus,
} from "@/lib/application/ports";
import { useApp } from "@/app/providers/app-provider";
import type { ResolvedTrackModel } from "@coffitivity/shared";
import type { IAudioRepository } from "@/lib/infrastructure/AudioRepository";
import type { AudioManager } from "@/lib/application/AudioManager";

interface AudioContextProps {
	audioManager: AudioManager;
	audioRepository: IAudioRepository;
	createAdapter: (
		track: ResolvedTrackModel,
	) => Promise<AudioPlaybackService | BlendedAudioPlaybackService>;
	currentTrack: ResolvedTrackModel | null;
	handleMuteToggle: () => void;
	handlePause: (baseTrackId?: string, blendTrackId?: string) => void;
	handleMasterVolumeChange: (volume: number) => void;
	handlePlay: (track: ResolvedTrackModel, blendTrackId?: string) => void;
	audioStatus: PlaybackStatus;
	masterVolume: number;
	isCurrentTrackDraft: boolean;
}

const AudioContext = createContext<AudioContextProps | undefined>(undefined);

export const useAudio = () => {
	const context = useContext(AudioContext);
	if (!context) {
		throw new Error("useAudio must be used within an AudioProvider");
	}
	return context;
};

const AudioProvider: React.FC<{ children: React.ReactNode }> = ({
	children,
}) => {
	const { audioManager } = useApp();
	const { audioRepository } = audioManager;
	const { currentTrack, masterVolume, status, isCurrentTrackDraft } =
		audioManager.useStore();
	// // User Engagement Handler
	// const initializeAudioOnUserGesture = useCallback(async () => {
	// 	// Only run once!
	// 	if (isUserEngaged) return;

	// 	setIsUserEngaged(true);
	// }, [isUserEngaged]);

	// // Wire up user gesture listeners early (only once)
	// useEffect(() => {
	// 	if (!isUserEngaged) {
	// 		window.addEventListener("click", initializeAudioOnUserGesture, {
	// 			once: true,
	// 		});
	// 		window.addEventListener("touchstart", initializeAudioOnUserGesture, {
	// 			once: true,
	// 		});
	// 	}
	// 	// cleanup in case component unmounts before engagement
	// 	return () => {
	// 		window.removeEventListener("click", initializeAudioOnUserGesture);
	// 		window.removeEventListener("touchstart", initializeAudioOnUserGesture);
	// 	};
	// }, [isUserEngaged, initializeAudioOnUserGesture]);

	// Keyboard
	// biome-ignore lint/correctness/useExhaustiveDependencies: handlePause changes on every render due to audioManager being a class instance
	const handleKeyboardEvents = useCallback(
		(event: KeyboardEvent) => {
			const target = event.target as HTMLElement;
			const isTypingElement = ["INPUT", "TEXTAREA", "SELECT"].includes(
				target.tagName,
			);
			if (isTypingElement) return;
			if (event.key === " " || event.code === "Space") {
				event.preventDefault();
				if (status === "PLAYING") {
					handlePause();
				} else {
					if (currentTrack) {
						handlePlay(currentTrack as ResolvedTrackModel);
					}
				}
			}
		},
		[status, currentTrack],
	);

	// Volume/mute logic
	const handleMasterVolumeChange = (volume: number) => {
		const normalizedVolume = volume > 1 ? volume / 100 : volume;
		audioManager?.setMasterVolume(normalizedVolume);
	};

	const handleMuteToggle = () => {
		audioManager?.toggleMute();
	};

	// Pause logic
	const handlePause = (baseTrackId?: string, blendTrackId?: string) => {
		audioManager?.pauseTrack(baseTrackId, blendTrackId);
	};

	// Play logic
	const handlePlay = async (
		track: ResolvedTrackModel,
		blendTrackId?: string,
	) => {
		// if (!isUserEngaged) {
		// 	await initializeAudioOnUserGesture();
		// }

		audioManager?.playTrack(track, blendTrackId);
	};

	// Adapter creation
	const handleAdapterCreation = async (track: ResolvedTrackModel) => {
		const adapter = await audioManager.getOrCreateAudioAdapter(track);

		return adapter;
	};

	// Keyboard events
	useEffect(() => {
		window.addEventListener("keydown", handleKeyboardEvents);
		return () => window.removeEventListener("keydown", handleKeyboardEvents);
	}, [handleKeyboardEvents]);

	return (
		<AudioContext.Provider
			value={{
				audioManager,
				audioRepository,
				audioStatus: status,
				createAdapter: handleAdapterCreation,
				currentTrack: currentTrack as ResolvedTrackModel | null,
				handleMasterVolumeChange,
				handleMuteToggle,
				handlePause,
				handlePlay,
				masterVolume,
				isCurrentTrackDraft,
			}}
		>
			{children}
		</AudioContext.Provider>
	);
};

export default AudioProvider;
